import React from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SuperadminLayout from './SuperadminLayout';
import { KNOWN_PATHS, HIDE_HEADER_ROUTES } from '../routes';
import { PUBLIC_ROUTE_PATHS, HIDE_SIDEBAR_ROUTES, OWN_SIDEBAR_ROUTES, SKIP_SESSION_MODAL_PREFIXES } from '../routes/routeMeta';
import { isSuperadminRoute } from '../routes/routeUtils';
import SessionTimeoutModal from '../components/authentication/SessionTimeoutModal';
import GlobalLicenseModal from '../components/common/GlobalLicenseModal';
import TawkTo from '../integrations/TawkTo';
import Header from '../components/common/Header';
import TimezoneMismatchBanner from '../components/common/TimezoneMismatchBanner';
import Sidebar from '../components/sidebar/Sidebar';
import useSessionManager from '../components/authentication/useSessionManager';
import { logActivity } from '../utils/logActivity';

const Layout = ({ children, selectedDropdown, setSelectedDropdown }) => {
  const location = useLocation();
  const { showModal, handleContinue, handleLogout } = useSessionManager();

  const isPublicRoute = PUBLIC_ROUTE_PATHS.includes(location.pathname);
  const isUnknownRoute = !KNOWN_PATHS.has(location.pathname);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const framework = params.get('framework');
    if (framework && framework !== selectedDropdown) {
      setSelectedDropdown(framework);
    }
  }, [location.search, selectedDropdown, setSelectedDropdown]);

  const hideHeaderRoutes = HIDE_HEADER_ROUTES;
  const hideSidebarRoutes = HIDE_SIDEBAR_ROUTES;
  const pathToSkipSession = SKIP_SESSION_MODAL_PREFIXES;

  const shouldHideSidebar = hideSidebarRoutes.includes(location.pathname);
  const shouldHideHeader = hideHeaderRoutes.includes(location.pathname);
  const shouldHideSessionModal =
    isUnknownRoute || location.pathname === '/404' ||
    pathToSkipSession.some(route => location.pathname.startsWith(route));

  // Pages with their own sidebar (e.g. the OffSec Staff Portal) start collapsed
  // too, matching the default for the shared CARA sidebar.
  const shouldCollapseSidebar =
    !shouldHideSidebar || OWN_SIDEBAR_ROUTES.includes(location.pathname);

  useEffect(() => {
    if (isPublicRoute) return;
    logActivity(location.pathname, 'Success');
  }, [isPublicRoute, location.pathname]);

  useEffect(() => {
    if (shouldCollapseSidebar) {
      document.body.classList.add('sidebar-collapse');
    } else {
      document.body.classList.remove('sidebar-collapse');
    }
  }, [location.pathname, shouldCollapseSidebar]);

  const useSuperadminLayout = isSuperadminRoute(location.pathname);

  return (
    <div className="app-container">
      <TawkTo />
      <GlobalLicenseModal />
      {/* Only on routes with the generic Header - HIDE_SIDEBAR_ROUTES pages
          (login screens, and the OffSec Staff Portal, which has no header
          for this banner to dock under) render their own chrome instead. */}
      {!isPublicRoute && !shouldHideSidebar && <TimezoneMismatchBanner />}
      {!shouldHideSidebar ? (
        useSuperadminLayout ? (
          <SuperadminLayout>{children}</SuperadminLayout>
        ) : (
          <>
            {!shouldHideHeader && (
              <Header
                selectedDropdown={selectedDropdown}
                setSelectedDropdown={setSelectedDropdown}
                currentPath={location.pathname}
              />
            )}
            <Sidebar />
            <main className="content">{children}</main>
          </>
        )
      ) : (
        <main className="content">{children}</main>
      )}

      {!isPublicRoute && !shouldHideSessionModal && (
        <SessionTimeoutModal
          show={showModal}
          onContinue={handleContinue}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
};

export default Layout;
